import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { colors, spacing, borderRadius, duration, easing } from 'ui/theme';
import { body2, caption } from 'ui/theme/typography';
import { Tooltip } from 'ui/tooltip';

export const OptionBadge = styled.div`
  ${caption}
  align-items: center;
  border-radius: ${borderRadius.full};
  border: solid ${spacing.single} ${colors.borderPrimary};
  display: flex;
  justify-content: center;
  padding: ${spacing.eighth} ${spacing.half};
  white-space: nowrap;
`;

export type ItemProps = {
  children?: ReactNode;
  isSelected?: boolean;
  isDisabled?: boolean;
  ref?: React.ForwardedRef<HTMLLIElement>;
};

export const OptionItem = styled.li<ItemProps>`
  ${({ isDisabled }) =>
    isDisabled
      ? css`
          border-color: transparent;
          opacity: 0.5;
          pointer-events: none;
        `
      : css`
          cursor: pointer;
        `}
  align-items: center;
  ${body2};
  border: solid ${spacing.single} transparent;
  border-radius: ${borderRadius.md};
  display: flex;
  justify-content: space-between;
  margin-bottom: ${spacing.single}; /** keeps focus state from causing overlaps */
  overflow: hidden;
  outline: none;
  padding: ${spacing.threeEights} ${spacing.half};
  transition: all ${duration.short} ${easing.exit};

  path {
    transition: all ${duration.short} ${easing.exit};
    :not(.useInlineFill) {
      fill: ${colors.iconPrimary};
    }
  }

  :hover {
    background: ${colors.backgroundPrimaryHover};
    transition: all ${duration.short} ${easing.entrance};

    path {
      transition: all ${duration.short} ${easing.entrance};
      :not(.useInlineFill) {
        fill: ${colors.iconEmphasis};
      }
    }
  }

  ${({ isSelected }) =>
    isSelected &&
    css`
      background: ${colors.backgroundInteractive};
      border-color: transparent;
      color: ${colors.textInteractiveHover};
      transition: all ${duration.short} ${easing.entrance};

      path {
        transition: all ${duration.short} ${easing.exit};
        :not(.useInlineFill) {
          fill: ${colors.iconInteractiveHover};
        }
      }

      :hover {
        background: ${colors.backgroundInteractive};

        path {
          fill: ${colors.iconInteractiveHover};
        }
      }
    `}
  :focus {
    border-color: ${colors.borderInteractive};
    box-shadow: 0px 0px 0px ${spacing.single} ${colors.borderInteractive};
  }
`;

export const OptionChild = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.eighth};
  overflow: hidden;
`;

export const LeftSection = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  overflow: hidden;
  padding: ${spacing.eighth} 0;

  > * + * {
    margin-left: ${spacing.half};
  }
`;

export const SuffixWrapper = styled.span`
  color: ${colors.textSecondary};
`;

export const OptionLoading = styled.div`
  color: ${colors.textSecondary};
  text-align: center;
`;

export const OptionTooltip = styled(Tooltip)<{ $isDisabled: boolean }>`
  && {
    cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
    display: block;
    max-width: none;
  }
`;

export const StyledOptionUsageOverflow = styled.div<{ top: number; left: number }>`
  position: absolute;
  top: ${(props) => (props?.top ? `${props.top}px` : 0)};
  left: ${(props) => (props?.left ? `${props.left}px` : 0)};
`;
